import { type ReactNode, useState } from "react";
import "./ScheduleImportMonthDialog.scss";
import CustomDialog from "@/shared/components/generic/custom-dialog/CustomDialog.tsx";
import IconCalendarIncomingArrow from "@/shared/components/generic/icons/IconCalendarIncomingArrow.tsx";
import { useTranslation } from "react-i18next";
import DialogActionsButtons from "@/shared/components/generic/dialog-actions-buttons/DialogActionsButtons.tsx";
import Button from "@/shared/components/generic/button/Button.tsx";
import { Level, Size } from "@/shared/components/generic/UiEnums.ts";
import useImportPreviousMonthSchedules from "@/pages/human-resources/time-management/schedules/site-schedule/month-view/import-dialog/useImportPreviousMonthSchedules.ts";
import ImportActionSelector from "@/pages/human-resources/time-management/components/schedule-import/ImportActionSelector.tsx";
import type { ImportAction } from "@/pages/human-resources/time-management/components/schedule-import/model/ImportAction.ts";
import Checkbox from "@/shared/components/generic/checkbox/Checkbox.tsx";

interface Properties {
  onClose: () => void;
}

export default function ScheduleImportMonthDialog(
  props: Properties,
): ReactNode {
  const { t: tCommon } = useTranslation("common");
  const { t: tDialog } = useTranslation("humanResources", {
    keyPrefix: "timeManagement.scheduleImportDialog",
  });
  const importPreviousMonth = useImportPreviousMonthSchedules();
  const [isImporting, setIsImporting] = useState(false);
  const [matchWeekDays, setMatchWeekDays] = useState(true);
  const [selectedAction, setSelectedAction] =
    useState<ImportAction>("fillEmpty");

  return (
    <CustomDialog
      open={true}
      onClose={props.onClose}
      title={tDialog("month.title")}
      icon={IconCalendarIncomingArrow}
    >
      <div className="schedule-import-month-dialog">
        <Checkbox
          onChange={(_, selected) => {
            setMatchWeekDays(selected);
          }}
          selected={matchWeekDays}
          item={{
            key: "matchWeekDays",
            label: tDialog("month.options.matchWeekDays"),
          }}
          size={Size.XSMALL}
        />

        <ImportActionSelector
          action={selectedAction}
          onChange={setSelectedAction}
        />

        <DialogActionsButtons>
          <Button
            onClick={props.onClose}
            label={tCommon("actions.cancel")}
            size={Size.SMALL}
            level={Level.SECONDARY}
          />
          <Button
            disabled={isImporting}
            onClick={() => {
              setIsImporting(true);
              void importPreviousMonth(selectedAction, matchWeekDays).finally(
                () => {
                  setIsImporting(false);
                  props.onClose();
                },
              );
            }}
            label={tCommon("actions.apply")}
            size={Size.SMALL}
            level={Level.PRIMARY}
          />
        </DialogActionsButtons>
      </div>
    </CustomDialog>
  );
}
